// Thai visa-type landing corpus.
// Sources of truth (verified Aug 2026): Immigration Bureau (immigration.go.th),
// MFA e-Visa (thaievisa.go.th), Department of Employment (doe.go.th), BOI / LTR (boi.go.th,
// ltr.boi.go.th). Requirements and permitted stay are always at the discretion of the
// deciding officer; figures below are the published thresholds, not a guarantee of approval.

export interface ThaiVisaType {
  slug: string;
  titleTh: string;
  titleEn: string;
  codeTh: string;
  codeEn: string;
  whoTh: string;
  whoEn: string;
  validityTh: string;
  validityEn: string;
  stayTh: string;
  stayEn: string;
  requirementsTh: string[];
  requirementsEn: string[];
  processTh: string[];
  processEn: string[];
  extensionTh: string;
  extensionEn: string;
  pitfallsTh: string[];
  pitfallsEn: string[];
  authorityTh: string;
  authorityEn: string;
  sources: { label: string; url: string }[];
}

const V = (v: ThaiVisaType): ThaiVisaType => v;

export const THAI_VISA_TYPES: ThaiVisaType[] = [
  V({
    slug: "tourist-tr",
    titleTh: "วีซ่าท่องเที่ยว (Tourist Visa – TR)",
    titleEn: "Thailand Tourist Visa (TR)",
    codeTh: "TR – Single Entry",
    codeEn: "TR – Single Entry",
    whoTh: "ผู้ที่เดินทางเข้าไทยเพื่อการท่องเที่ยวหรือเยี่ยมเยียน และสัญชาติที่ไม่ได้รับสิทธิยกเว้นการตรวจลงตรา หรือผู้ที่ต้องการอยู่นานกว่าสิทธิยกเว้น",
    whoEn: "Travellers visiting Thailand for tourism or to visit friends and family, especially nationalities without visa exemption or those who need longer than the exemption allows.",
    validityTh: "ตราวีซ่ามีอายุใช้เดินทางเข้าไทยได้ภายใน 3 เดือนนับจากวันออกวีซ่า (บางกรณี 6 เดือน) — อายุวีซ่าไม่ใช่จำนวนวันที่อยู่ได้",
    validityEn: "The visa is normally valid for entry within 3 months of issue (6 months in some cases). Visa validity is not the same as your permitted length of stay.",
    stayTh: "เจ้าหน้าที่ตรวจคนเข้าเมืองมักประทับอนุญาตให้อยู่ได้ไม่เกิน 60 วันต่อการเข้าหนึ่งครั้ง",
    stayEn: "Immigration officers typically stamp permission to stay of up to 60 days per entry.",
    requirementsTh: [
      "หนังสือเดินทางอายุเหลือไม่น้อยกว่า 6 เดือน และมีหน้าว่างสำหรับตราประทับ",
      "รูปถ่ายตามข้อกำหนดของระบบ e-Visa (พื้นหลังขาว ถ่ายไม่เกิน 6 เดือน)",
      "หลักฐานการเดินทาง เช่น การจองตั๋วเครื่องบิน และหลักฐานที่พักในไทย",
      "หลักฐานทางการเงินตามที่สถานเอกอัครราชทูต/สถานกงสุลกำหนด",
      "กรอกบัตรขาเข้าดิจิทัล TDAC ก่อนเดินทางถึงไทย (คนละระบบกับวีซ่า)",
    ],
    requirementsEn: [
      "Passport valid at least 6 months with blank pages for stamps",
      "Photograph meeting e-Visa specifications (white background, taken within 6 months)",
      "Travel evidence such as flight booking and proof of accommodation in Thailand",
      "Proof of funds as specified by the Thai embassy or consulate handling your file",
      "Complete the TDAC digital arrival card before arrival — a separate system from the visa",
    ],
    processTh: [
      "สมัครบัญชีและยื่นคำร้องออนไลน์ที่ระบบ e-Visa ของกระทรวงการต่างประเทศ (thaievisa.go.th)",
      "เลือกประเทศที่พำนักและสถานเอกอัครราชทูต/สถานกงสุลที่รับผิดชอบเขตนั้น",
      "อัปโหลดเอกสารและชำระค่าธรรมเนียมตามที่ระบบแจ้ง",
      "รอผลทางอีเมลและพิมพ์หรือบันทึกไฟล์ e-Visa ไว้แสดงที่ด่านตรวจคนเข้าเมือง",
    ],
    processEn: [
      "Create an account and file online through the MFA e-Visa system (thaievisa.go.th)",
      "Select your country of residence and the Thai mission responsible for that jurisdiction",
      "Upload the documents and pay the fee quoted by the system",
      "Receive the result by email; print or save the e-Visa PDF to show at immigration",
    ],
    extensionTh: "ขออยู่ต่อได้อีกไม่เกิน 30 วันโดยยื่นแบบ ตม.7 ที่สำนักงานตรวจคนเข้าเมืองในพื้นที่ก่อนวันอนุญาตหมดอายุ การอนุมัติเป็นดุลพินิจของพนักงานเจ้าหน้าที่",
    extensionEn: "You may apply for an extension of up to 30 days by filing form TM.7 at your local immigration office before your permitted stay expires. Approval is at the officer's discretion.",
    pitfallsTh: [
      "เข้าใจผิดว่าอายุวีซ่า 3 เดือน = อยู่ได้ 3 เดือน แต่ความจริงยึดตราประทับขาเข้า",
      "ใช้วีซ่าท่องเที่ยวทำงาน แม้เป็นงานออนไลน์ที่มีลูกค้าในไทย เสี่ยงผิดกฎหมายแรงงาน",
      "ลืมกรอก TDAC หรือกรอกข้อมูลที่พักไม่ตรงกับที่อยู่จริง",
      "อยู่เกินกำหนด (overstay) แม้เพียงวันเดียวก็มีผลต่อประวัติการเข้าเมืองครั้งถัดไป",
    ],
    pitfallsEn: [
      "Assuming a 3-month visa validity means 3 months of stay — the entry stamp governs",
      "Working on a tourist visa, including remote work with Thai clients, risks labour-law breaches",
      "Skipping TDAC or entering accommodation details that do not match the real address",
      "Overstaying even by one day, which is recorded and affects future entries",
    ],
    authorityTh: "สถานเอกอัครราชทูต/สถานกงสุลไทย (ออกวีซ่า) และสำนักงานตรวจคนเข้าเมือง (อนุญาตเข้าเมืองและอยู่ต่อ)",
    authorityEn: "Thai embassies and consulates issue the visa; the Immigration Bureau grants entry and extensions.",
    sources: [
      { label: "Thai e-Visa (MFA)", url: "https://www.thaievisa.go.th" },
      { label: "Immigration Bureau", url: "https://www.immigration.go.th" },
    ],
  }),
  V({
    slug: "metv",
    titleTh: "วีซ่าท่องเที่ยวหลายครั้ง (METV)",
    titleEn: "Multiple-Entry Tourist Visa (METV)",
    codeTh: "TR – Multiple Entry",
    codeEn: "TR – Multiple Entry",
    whoTh: "ผู้ที่ต้องเดินทางเข้า–ออกไทยหลายครั้งภายในระยะเวลาหนึ่งปีเพื่อการท่องเที่ยว โดยยื่นจากประเทศที่มีถิ่นพำนัก",
    whoEn: "Travellers who need repeated tourist entries to Thailand within a set period, filed from their country of residence.",
    validityTh: "อายุวีซ่าโดยทั่วไป 6 เดือน เข้าออกได้ไม่จำกัดจำนวนครั้งภายในอายุวีซ่า",
    validityEn: "Typically 6 months of visa validity with unlimited entries during that window.",
    stayTh: "แต่ละครั้งที่เข้าประเทศ มักได้รับอนุญาตให้อยู่ไม่เกิน 60 วัน",
    stayEn: "Each entry normally receives permission to stay of up to 60 days.",
    requirementsTh: [
      "หลักฐานถิ่นพำนักในประเทศที่ยื่นคำร้อง",
      "หลักฐานการเงินย้อนหลังตามที่สถานทูตกำหนด (มักขอ statement ย้อนหลัง 6 เดือน)",
      "แผนการเดินทางที่แสดงเหตุผลของการเข้า–ออกหลายครั้ง",
      "หนังสือเดินทางอายุคงเหลือครอบคลุมตลอดอายุวีซ่าที่ขอ",
    ],
    requirementsEn: [
      "Proof of residence in the country where you file",
      "Financial evidence as required by that mission (commonly 6 months of bank statements)",
      "A travel plan showing why multiple entries are needed",
      "Passport validity covering the full requested visa period",
    ],
    processTh: [
      "ตรวจว่าสถานเอกอัครราชทูต/สถานกงสุลในเขตของท่านเปิดรับ METV หรือไม่ (ไม่ใช่ทุกแห่ง)",
      "ยื่นผ่านระบบ e-Visa พร้อมเอกสารการเงินและถิ่นพำนัก",
      "รอผลทางอีเมล และตรวจเงื่อนไขจำนวนวันต่อการเข้าหนึ่งครั้งบนหน้าวีซ่า",
    ],
    processEn: [
      "Check whether the Thai mission in your jurisdiction issues METV — not all do",
      "File through the e-Visa system with financial and residence evidence",
      "On approval, read the per-entry stay condition printed on the visa",
    ],
    extensionTh: "แต่ละครั้งที่เข้าประเทศสามารถยื่น ตม.7 ขออยู่ต่อได้อีกไม่เกิน 30 วัน แต่การใช้ METV เพื่ออยู่ยาวต่อเนื่องอาจถูกซักถามที่ด่าน",
    extensionEn: "Each entry may be extended by up to 30 days on form TM.7, but using METV for continuous long-term residence often draws questions at the border.",
    pitfallsTh: [
      "ยื่นจากประเทศที่ไม่ใช่ถิ่นพำนัก ทำให้ถูกปฏิเสธ",
      "เข้าใจว่าวีซ่า 6 เดือนคืออยู่ต่อเนื่อง 6 เดือน",
      "การเข้าครั้งสุดท้ายก่อนวีซ่าหมดอายุ ยังนับ 60 วันจากวันเข้า ไม่ใช่หมดพร้อมวีซ่า",
    ],
    pitfallsEn: [
      "Filing outside your country of residence, a common ground for refusal",
      "Treating 6 months of validity as 6 months of continuous stay",
      "Forgetting that the last entry before expiry still receives its own stay period from the entry date",
    ],
    authorityTh: "สถานเอกอัครราชทูต/สถานกงสุลไทยในต่างประเทศ",
    authorityEn: "Thai embassies and consulates abroad.",
    sources: [
      { label: "Thai e-Visa (MFA)", url: "https://www.thaievisa.go.th" },
      { label: "Immigration Bureau", url: "https://www.immigration.go.th" },
    ],
  }),
  V({
    slug: "visa-exemption",
    titleTh: "การยกเว้นการตรวจลงตรา (Visa Exemption) และ Visa on Arrival",
    titleEn: "Visa Exemption & Visa on Arrival",
    codeTh: "ผ.30 / ผ.60 / VOA",
    codeEn: "Visa exemption / VOA",
    whoTh: "ผู้ถือหนังสือเดินทางของสัญชาติที่ได้รับสิทธิยกเว้นการตรวจลงตราตามประกาศกระทรวงมหาดไทย และผู้มีสิทธิขอรับการตรวจลงตรา ณ ช่องทางอนุญาตของด่านตรวจคนเข้าเมือง",
    whoEn: "Passport holders of nationalities granted visa exemption by Ministry of Interior announcement, and nationalities eligible for visa on arrival at designated checkpoints.",
    validityTh: "ไม่ต้องขอวีซ่าล่วงหน้า แต่สิทธิและจำนวนวันขึ้นกับสัญชาติและประกาศที่บังคับใช้ ณ วันเดินทาง",
    validityEn: "No advance visa is needed, but entitlement and length depend on nationality and the announcement in force on the travel date.",
    stayTh: "สัญชาติในกลุ่มยกเว้นการตรวจลงตราส่วนใหญ่ได้รับอนุญาตให้อยู่ได้ไม่เกิน 60 วัน ส่วน Visa on Arrival ได้ไม่เกิน 15 วัน",
    stayEn: "Most exempted nationalities receive up to 60 days; visa on arrival grants up to 15 days.",
    requirementsTh: [
      "หนังสือเดินทางอายุเหลือไม่น้อยกว่า 6 เดือน",
      "หลักฐานการเดินทางออกนอกราชอาณาจักรภายในระยะเวลาที่ได้รับอนุญาต",
      "หลักฐานที่พักและหลักฐานการเงินที่เจ้าหน้าที่อาจขอตรวจ",
      "บัตรขาเข้าดิจิทัล TDAC ที่กรอกก่อนเดินทางถึง",
    ],
    requirementsEn: [
      "Passport valid at least 6 months",
      "Evidence of onward travel within the permitted period",
      "Accommodation and financial evidence, which officers may inspect",
      "TDAC digital arrival card completed before arrival",
    ],
    processTh: [
      "ตรวจสิทธิสัญชาติของท่านจากประกาศล่าสุดก่อนออกเดินทาง",
      "กรอก TDAC ภายในกรอบเวลาที่ระบบกำหนดก่อนถึงไทย",
      "แสดงหนังสือเดินทางและหลักฐานที่เกี่ยวข้องต่อพนักงานเจ้าหน้าที่ที่ด่าน",
    ],
    processEn: [
      "Confirm your nationality's entitlement under the current announcement before travelling",
      "Submit TDAC within the window required before arrival",
      "Present your passport and supporting evidence to the immigration officer",
    ],
    extensionTh: "ผู้ได้รับยกเว้นการตรวจลงตราสามารถยื่น ตม.7 ขออยู่ต่อได้อีกไม่เกิน 30 วัน ส่วน Visa on Arrival ขยายได้ไม่เกิน 7 วัน ตามดุลพินิจเจ้าหน้าที่",
    extensionEn: "Visa-exempt entries can be extended by up to 30 days on TM.7; visa on arrival by up to 7 days, at the officer's discretion.",
    pitfallsTh: [
      "เข้าไทยด้วยสิทธิยกเว้นบ่อยครั้งทางด่านพรมแดนทางบก อาจถูกจำกัดหรือปฏิเสธ",
      "ใช้สิทธิยกเว้นแล้วต้องการทำงาน — ต้องเปลี่ยนประเภทการตรวจลงตราให้ถูกต้องก่อน",
      "ไม่ตรวจประกาศฉบับล่าสุด เพราะรายชื่อสัญชาติและจำนวนวันปรับปรุงเป็นระยะ",
    ],
    pitfallsEn: [
      "Frequent visa-exempt entries by land can be limited or refused",
      "Wanting to work after a visa-exempt entry — the visa category must be changed first",
      "Relying on outdated lists; nationality entitlements and day counts are revised periodically",
    ],
    authorityTh: "สำนักงานตรวจคนเข้าเมือง ตามประกาศกระทรวงมหาดไทย",
    authorityEn: "Immigration Bureau, under Ministry of Interior announcements.",
    sources: [
      { label: "Immigration Bureau", url: "https://www.immigration.go.th" },
      { label: "Ministry of Foreign Affairs", url: "https://www.mfa.go.th" },
    ],
  }),
  V({
    slug: "non-b",
    titleTh: "วีซ่าธุรกิจ/ทำงาน Non-Immigrant B",
    titleEn: "Non-Immigrant B (Business & Work)",
    codeTh: "Non-B",
    codeEn: "Non-B",
    whoTh: "ชาวต่างชาติที่จะทำงาน ประกอบธุรกิจ หรือเข้าประชุมกับนิติบุคคลในไทย โดยมีนายจ้างหรือคู่ธุรกิจในไทยเป็นผู้สนับสนุนเอกสาร",
    whoEn: "Foreign nationals coming to work, run a business or attend business meetings with a Thai entity, supported by a Thai employer or business partner.",
    validityTh: "วีซ่าเข้าครั้งเดียวมีอายุใช้เดินทาง 3 เดือน แบบเข้าหลายครั้ง (Multiple) มีอายุ 1 ปี",
    validityEn: "Single-entry Non-B is valid for travel within 3 months; the multiple-entry version is valid for 1 year.",
    stayTh: "ได้รับอนุญาตให้อยู่ครั้งละไม่เกิน 90 วัน แล้วขออยู่ต่อรายปีในไทยเมื่อมีใบอนุญาตทำงาน",
    stayEn: "Each entry gives up to 90 days; a one-year extension can then be requested in Thailand once the work permit is in place.",
    requirementsTh: [
      "หนังสือรับรองการจ้างงานหรือหนังสือเชิญจากนิติบุคคลไทย",
      "หนังสือรับรองการจดทะเบียนนิติบุคคล บัญชีรายชื่อผู้ถือหุ้น และงบการเงินของนายจ้าง",
      "แบบ ตท.3 (WP.3) หรือเอกสารประกอบการขอใบอนุญาตทำงานตามที่กรมการจัดหางานกำหนด",
      "วุฒิการศึกษาและหนังสือรับรองประสบการณ์ ซึ่งมักต้องผ่านการรับรองเอกสารและแปล",
      "หนังสือเดินทางอายุคงเหลือครอบคลุมระยะเวลาที่ขอ",
    ],
    requirementsEn: [
      "Employment certificate or invitation letter from the Thai entity",
      "Employer's company affidavit, shareholder list and financial statements",
      "Form WP.3 or the supporting set required by the Department of Employment",
      "Degree certificates and experience letters, usually requiring certification and translation",
      "Passport validity covering the requested period",
    ],
    processTh: [
      "นายจ้างเตรียมชุดเอกสารบริษัทและหนังสือรับรองการจ้าง",
      "ยื่นคำร้องขอวีซ่า Non-B ผ่านระบบ e-Visa จากต่างประเทศ",
      "เข้าประเทศแล้วยื่นขอใบอนุญาตทำงานที่กรมการจัดหางานหรือสำนักงานจัดหางานจังหวัด",
      "เมื่อได้ใบอนุญาตทำงาน จึงยื่น ตม.7 ขออยู่ต่อ 1 ปีที่สำนักงานตรวจคนเข้าเมือง",
      "รายงานตัว 90 วันตลอดระยะเวลาที่พำนัก",
    ],
    processEn: [
      "The employer prepares the corporate set and employment certificate",
      "Apply for the Non-B through the e-Visa system from abroad",
      "After entry, apply for the work permit at the Department of Employment or provincial office",
      "With the work permit issued, file TM.7 for a one-year extension of stay",
      "File 90-day reports throughout your stay",
    ],
    extensionTh: "ขออยู่ต่อครั้งละ 1 ปีด้วยแบบ ตม.7 โดยยึดเงื่อนไขทุนจดทะเบียน อัตราส่วนพนักงานไทยต่อชาวต่างชาติ และรายได้ขั้นต่ำตามสัญชาติที่สำนักงานตรวจคนเข้าเมืองกำหนด",
    extensionEn: "One-year extensions on TM.7, subject to registered capital, Thai-to-foreign staff ratio and the minimum salary table applied by the Immigration Bureau.",
    pitfallsTh: [
      "เริ่มทำงานก่อนใบอนุญาตทำงานออก ถือว่าทำงานโดยไม่ได้รับอนุญาต",
      "ทำงานนอกขอบเขตตำแหน่งหรือสถานที่ที่ระบุในใบอนุญาต",
      "เอกสารวุฒิการศึกษาไม่ได้ผ่านนิติกรณ์และแปลตามที่หน่วยงานกำหนด",
      "นายจ้างมีอัตราส่วนพนักงานไทยหรือทุนจดทะเบียนไม่ถึงเกณฑ์ ทำให้ต่ออายุไม่ผ่าน",
    ],
    pitfallsEn: [
      "Starting work before the work permit is issued — that is unauthorised employment",
      "Working outside the job title or workplace stated on the permit",
      "Degree documents not legalised and translated as required",
      "Employer failing the Thai-staff ratio or capital thresholds at extension time",
    ],
    authorityTh: "กรมการจัดหางาน (ใบอนุญาตทำงาน) และสำนักงานตรวจคนเข้าเมือง (การอยู่ต่อ)",
    authorityEn: "Department of Employment for the work permit; Immigration Bureau for stay.",
    sources: [
      { label: "Department of Employment", url: "https://www.doe.go.th" },
      { label: "Immigration Bureau", url: "https://www.immigration.go.th" },
      { label: "Thai e-Visa (MFA)", url: "https://www.thaievisa.go.th" },
    ],
  }),
  V({
    slug: "non-ed",
    titleTh: "วีซ่านักเรียน/ฝึกอบรม Non-Immigrant ED",
    titleEn: "Non-Immigrant ED (Study & Training)",
    codeTh: "Non-ED",
    codeEn: "Non-ED",
    whoTh: "ผู้ที่เข้ามาศึกษาในสถาบันการศึกษาที่ได้รับการรับรอง เข้ารับการฝึกอบรม ฝึกงาน หรือศึกษาธรรมะตามที่หน่วยงานต้นสังกัดรับรอง",
    whoEn: "Students at accredited institutions, trainees, interns and those undertaking approved religious or specialised study.",
    validityTh: "อายุวีซ่าใช้เดินทาง 3 เดือน (หรือมากกว่าในบางกรณี) และได้รับอนุญาตให้อยู่ครั้งแรก 90 วัน",
    validityEn: "Usually 3 months of travel validity, with an initial 90-day permission to stay.",
    stayTh: "ต่ออายุการอยู่ต่อเป็นช่วง ๆ ตามหนังสือรับรองของสถาบันและหลักสูตร",
    stayEn: "Extended in stages against the institution's certification and course length.",
    requirementsTh: [
      "หนังสือตอบรับจากสถาบันการศึกษา พร้อมหนังสือรับรองจากหน่วยงานที่กำกับดูแล",
      "หลักฐานการชำระค่าเล่าเรียนและหลักฐานทางการเงินสำหรับค่าครองชีพ",
      "หนังสือเดินทางอายุคงเหลือเพียงพอ",
      "เอกสารประวัติการศึกษาที่อาจต้องแปลและรับรอง",
    ],
    requirementsEn: [
      "Acceptance letter from the institution plus approval from its supervising authority",
      "Proof of tuition payment and living funds",
      "Passport with sufficient remaining validity",
      "Academic records, which may require translation and certification",
    ],
    processTh: [
      "สถาบันออกหนังสือตอบรับและหนังสือรับรองต่อกระทรวง/หน่วยงานกำกับ",
      "ยื่นคำร้อง Non-ED ผ่านระบบ e-Visa จากต่างประเทศ",
      "เข้าประเทศแล้วรายงานตัวกับสถาบัน และยื่น ตม.7 ขออยู่ต่อก่อนวันอนุญาตหมด",
      "รายงานตัว 90 วัน และแจ้งที่พัก ตม.30 ตามหน้าที่ของเจ้าบ้าน",
    ],
    processEn: [
      "The institution issues the acceptance letter and regulator approval",
      "File the Non-ED through the e-Visa system from abroad",
      "After entry, register with the institution and file TM.7 before your stay expires",
      "Keep up 90-day reporting; the housemaster files TM30",
    ],
    extensionTh: "ขออยู่ต่อโดยยื่น ตม.7 พร้อมหนังสือรับรองการเป็นนักเรียนและผลการเข้าเรียนที่เป็นปัจจุบัน ระยะเวลาที่อนุญาตขึ้นกับประเภทหลักสูตร",
    extensionEn: "Extensions on TM.7 with a current enrolment certificate and attendance record; the period granted depends on the course type.",
    pitfallsTh: [
      "ขาดเรียนจนสถาบันไม่ออกหนังสือรับรอง ทำให้ต่ออายุไม่ได้",
      "เข้าใจผิดว่า Non-ED ทำงานได้ — การทำงานต้องมีใบอนุญาตทำงานแยกต่างหาก",
      "เลือกสถาบันที่ไม่ได้รับการรับรองจากหน่วยงานกำกับ",
    ],
    pitfallsEn: [
      "Poor attendance, so the school cannot certify you for the extension",
      "Assuming Non-ED allows work — employment needs a separate work permit",
      "Choosing a school without proper regulator accreditation",
    ],
    authorityTh: "สถาบันการศึกษาและหน่วยงานกำกับ ร่วมกับสำนักงานตรวจคนเข้าเมือง",
    authorityEn: "The institution and its regulator, together with the Immigration Bureau.",
    sources: [
      { label: "Immigration Bureau", url: "https://www.immigration.go.th" },
      { label: "Thai e-Visa (MFA)", url: "https://www.thaievisa.go.th" },
    ],
  }),
  V({
    slug: "non-o-marriage",
    titleTh: "วีซ่าติดตามคู่สมรสไทย Non-Immigrant O (สมรส)",
    titleEn: "Non-Immigrant O — Marriage to a Thai National",
    codeTh: "Non-O (Marriage)",
    codeEn: "Non-O (Marriage)",
    whoTh: "ชาวต่างชาติที่จดทะเบียนสมรสกับผู้มีสัญชาติไทยและประสงค์จะพำนักในไทยกับครอบครัว",
    whoEn: "Foreign nationals legally married to a Thai citizen who wish to live in Thailand with their family.",
    validityTh: "วีซ่าเข้าครั้งเดียวใช้เดินทางภายใน 3 เดือน อนุญาตให้อยู่ครั้งแรก 90 วัน",
    validityEn: "Single-entry validity of 3 months with an initial 90-day permission to stay.",
    stayTh: "ขออยู่ต่อรายปีในไทยได้เมื่อผ่านเกณฑ์ทางการเงินและการตรวจสอบความสัมพันธ์",
    stayEn: "Annual extensions inside Thailand once the financial test and relationship checks are satisfied.",
    requirementsTh: [
      "ทะเบียนสมรส (คร.2) และทะเบียนฐานะแห่งครอบครัวหากจดทะเบียนในต่างประเทศ",
      "ทะเบียนบ้านและบัตรประชาชนของคู่สมรสไทย",
      "หลักฐานการเงินตามเกณฑ์ที่ตรวจคนเข้าเมืองกำหนด — เงินฝากในธนาคารไทยในชื่อผู้ยื่น 400,000 บาท ฝากต่อเนื่องตามระยะเวลาที่กำหนด หรือรายได้ประจำเดือนละ 40,000 บาท",
      "ภาพถ่ายที่พักอาศัยร่วมกันและแผนที่บ้าน ตามที่แต่ละพื้นที่ร้องขอ",
      "แบบ ตม.7 พร้อมสำเนาหนังสือเดินทางทุกหน้าที่เกี่ยวข้อง",
    ],
    requirementsEn: [
      "Marriage certificate (Kor Ror 2) and family status registration if married abroad",
      "Thai spouse's house registration and ID card",
      "Financial evidence per Immigration criteria — THB 400,000 held in a Thai bank account in the applicant's name for the required seasoning period, or monthly income of THB 40,000",
      "Photographs of the shared home and a map, as required locally",
      "Form TM.7 with copies of the relevant passport pages",
    ],
    processTh: [
      "จดทะเบียนสมรสให้ถูกต้อง หากสมรสต่างประเทศต้องผ่านนิติกรณ์และบันทึกฐานะแห่งครอบครัวที่อำเภอ",
      "ยื่นขอวีซ่า Non-O จากต่างประเทศ หรือเปลี่ยนประเภทการตรวจลงตราในไทยหากคุณสมบัติครบ",
      "จัดเตรียมบัญชีเงินฝากให้ครบระยะเวลาก่อนวันยื่นขออยู่ต่อ",
      "ยื่น ตม.7 ที่สำนักงานตรวจคนเข้าเมืองในพื้นที่ พร้อมสัมภาษณ์และตรวจเยี่ยมบ้านตามกรณี",
    ],
    processEn: [
      "Register the marriage correctly; overseas marriages must be legalised and recorded at the district office",
      "Apply for the Non-O abroad, or change visa category in Thailand if eligible",
      "Season the bank balance for the required period before filing",
      "File TM.7 at your local immigration office; interviews and home visits are common",
    ],
    extensionTh: "ขออยู่ต่อปีต่อปีด้วยเหตุผลสมรสกับคนไทย โดยต้องรักษาเงื่อนไขการเงินและความเป็นครอบครัวตลอดเวลา การอนุมัติเป็นดุลพินิจของพนักงานเจ้าหน้าที่",
    extensionEn: "Year-by-year extensions on marriage grounds; the financial and family conditions must be maintained throughout. Approval remains discretionary.",
    pitfallsTh: [
      "เงินฝากไม่ครบระยะเวลาที่กำหนดก่อนวันยื่น",
      "ทะเบียนสมรสต่างประเทศยังไม่ได้บันทึกฐานะแห่งครอบครัวที่อำเภอ",
      "เข้าใจว่า Non-O สมรสทำงานได้ทันที — การทำงานต้องมีใบอนุญาตทำงานแยก",
      "ย้ายที่อยู่แล้วไม่แจ้ง ตม.30 ทำให้เอกสารที่อยู่ไม่ตรงกัน",
    ],
    pitfallsEn: [
      "Bank funds not seasoned for the required period before filing",
      "Overseas marriage not yet recorded at the Thai district office",
      "Assuming marriage status alone permits work — a work permit is still required",
      "Moving house without a TM30 filing, creating inconsistent address records",
    ],
    authorityTh: "สำนักงานตรวจคนเข้าเมือง ร่วมกับสำนักทะเบียนอำเภอ/เขต",
    authorityEn: "Immigration Bureau together with the district registrar.",
    sources: [
      { label: "Immigration Bureau", url: "https://www.immigration.go.th" },
      { label: "Consular Affairs (MFA)", url: "https://consular.mfa.go.th" },
    ],
  }),
  V({
    slug: "non-o-retirement",
    titleTh: "วีซ่าเกษียณอายุ Non-Immigrant O (อายุ 50 ปีขึ้นไป)",
    titleEn: "Non-Immigrant O — Retirement (Age 50+)",
    codeTh: "Non-O (Retirement)",
    codeEn: "Non-O (Retirement)",
    whoTh: "ชาวต่างชาติอายุ 50 ปีบริบูรณ์ขึ้นไปที่ต้องการใช้ชีวิตหลังเกษียณในไทย โดยไม่ประสงค์จะทำงาน",
    whoEn: "Foreign nationals aged 50 or over who wish to retire in Thailand and will not work.",
    validityTh: "วีซ่า Non-O อนุญาตให้อยู่ครั้งแรก 90 วัน แล้วยื่นขออยู่ต่อ 1 ปีในไทย",
    validityEn: "The Non-O grants an initial 90 days, after which a one-year extension is filed in Thailand.",
    stayTh: "ขออยู่ต่อปีละครั้งด้วยเหตุผลเกษียณอายุ",
    stayEn: "Renewed annually on retirement grounds.",
    requirementsTh: [
      "อายุ 50 ปีบริบูรณ์ขึ้นไป ณ วันยื่นคำขออยู่ต่อ",
      "เงินฝากในธนาคารไทยในชื่อผู้ยื่น 800,000 บาท ฝากต่อเนื่องตามระยะเวลาที่กำหนดก่อนและหลังการอนุญาต หรือรายได้/บำนาญเดือนละ 65,000 บาท หรือใช้วิธีผสมให้ครบ 800,000 บาทต่อปี",
      "หนังสือรับรองยอดเงินฝากจากธนาคารและสมุดบัญชีที่ปรับปรุงเป็นปัจจุบัน",
      "แบบ ตม.7 พร้อมรูปถ่ายและสำเนาหนังสือเดินทาง",
      "ที่พักอาศัยในไทยพร้อมหลักฐานการแจ้ง ตม.30",
    ],
    requirementsEn: [
      "Aged 50 or over on the date of the extension application",
      "THB 800,000 in a Thai bank account in your own name, seasoned for the periods required before and after approval, or monthly income/pension of THB 65,000, or a combination totalling THB 800,000 per year",
      "Bank letter confirming the balance plus an updated passbook",
      "Form TM.7 with photographs and passport copies",
      "A Thai address supported by the TM30 filing",
    ],
    processTh: [
      "ยื่นขอวีซ่า Non-O จากต่างประเทศผ่าน e-Visa หรือเปลี่ยนประเภทการตรวจลงตราในไทยหากคุณสมบัติครบ",
      "เปิดบัญชีธนาคารไทยและฝากเงินให้ครบระยะเวลาก่อนยื่น",
      "ยื่น ตม.7 ที่สำนักงานตรวจคนเข้าเมืองในพื้นที่ก่อนวันอนุญาตหมดอายุ",
      "หลังได้รับอนุญาต ขอ Re-entry Permit หากต้องเดินทางออกนอกประเทศ และรายงานตัว 90 วัน",
    ],
    processEn: [
      "Apply for the Non-O abroad through e-Visa, or change category in Thailand if eligible",
      "Open a Thai bank account and season the funds for the required period",
      "File TM.7 at your local immigration office before your stay expires",
      "Obtain a re-entry permit before travelling abroad and keep up 90-day reporting",
    ],
    extensionTh: "ต่ออายุการอยู่ต่อปีละครั้ง โดยต้องคงยอดเงินฝากตามเงื่อนไขหลังการอนุญาตด้วย ไม่ใช่เพียงวันยื่น",
    extensionEn: "Renewed each year; the balance must also be maintained after approval, not only on the filing date.",
    pitfallsTh: [
      "ถอนเงินออกหลังได้รับอนุญาต ทำให้ผิดเงื่อนไขในการต่ออายุปีถัดไป",
      "ใช้บัญชีร่วมกับคู่สมรส — ต้องเป็นบัญชีในชื่อผู้ยื่นเท่านั้น",
      "เดินทางออกนอกประเทศโดยไม่ทำ Re-entry Permit ทำให้การอนุญาตอยู่ต่อสิ้นสุด",
      "ลืมรายงานตัว 90 วัน ซึ่งเป็นคนละเรื่องกับการต่อวีซ่า",
    ],
    pitfallsEn: [
      "Withdrawing the funds after approval, breaching the condition for the next renewal",
      "Using a joint account — the account must be in the applicant's sole name",
      "Leaving Thailand without a re-entry permit, which cancels the permission to stay",
      "Missing 90-day reports, which are separate from the annual extension",
    ],
    authorityTh: "สำนักงานตรวจคนเข้าเมือง",
    authorityEn: "Immigration Bureau.",
    sources: [
      { label: "Immigration Bureau", url: "https://www.immigration.go.th" },
      { label: "Thai e-Visa (MFA)", url: "https://www.thaievisa.go.th" },
    ],
  }),
  V({
    slug: "o-a-retirement",
    titleTh: "วีซ่าพำนักระยะยาว O-A (Long Stay 1 ปี)",
    titleEn: "Non-Immigrant O-A Long Stay (1 Year)",
    codeTh: "Non-O-A",
    codeEn: "Non-O-A",
    whoTh: "ผู้มีอายุ 50 ปีขึ้นไปที่ยื่นคำร้องจากต่างประเทศ ณ สถานเอกอัครราชทูต/สถานกงสุลไทย เพื่อพำนักระยะยาวแบบเข้าออกได้หลายครั้ง",
    whoEn: "Applicants aged 50+ filing from abroad at a Thai mission for a one-year, multiple-entry long-stay visa.",
    validityTh: "อายุวีซ่า 1 ปี เข้าออกได้หลายครั้ง แต่ละครั้งได้รับอนุญาตให้อยู่ไม่เกิน 1 ปีนับจากวันเข้า",
    validityEn: "One year of validity with multiple entries; each entry receives up to one year of stay from the entry date.",
    stayTh: "ยื่นขออยู่ต่อในไทยได้เมื่อวีซ่าใกล้สิ้นสุด โดยใช้หลักเกณฑ์เกษียณอายุในประเทศ",
    stayEn: "Extensions inside Thailand are then handled under the domestic retirement criteria.",
    requirementsTh: [
      "อายุ 50 ปีขึ้นไป และไม่มีประวัติอาชญากรรม (หนังสือรับรองความประพฤติจากประเทศที่พำนัก)",
      "หลักฐานการเงิน: เงินฝาก 800,000 บาท หรือรายได้เดือนละ 65,000 บาท หรือรวมกันได้ 800,000 บาทต่อปี",
      "ใบรับรองแพทย์ตามแบบที่กำหนด",
      "กรมธรรม์ประกันสุขภาพที่ครอบคลุมตลอดระยะเวลาพำนักตามวงเงินที่ทางการกำหนด",
      "หนังสือเดินทางอายุคงเหลือไม่น้อยกว่า 18 เดือนในหลายสถานทูต",
    ],
    requirementsEn: [
      "Aged 50+ with a police clearance certificate from your country of residence",
      "Financial evidence: THB 800,000 on deposit, monthly income of THB 65,000, or a combination reaching THB 800,000 per year",
      "Medical certificate in the prescribed form",
      "Health insurance covering the whole stay at the coverage level required by the authorities",
      "Passport validity of at least 18 months at many missions",
    ],
    processTh: [
      "ขอหนังสือรับรองความประพฤติและใบรับรองแพทย์จากประเทศที่พำนัก",
      "ซื้อกรมธรรม์ประกันสุขภาพที่ตรงตามเงื่อนไขและตรวจสอบได้",
      "ยื่นคำร้องผ่านระบบ e-Visa ของสถานเอกอัครราชทูต/สถานกงสุลในเขตถิ่นพำนัก",
      "เมื่อเข้าประเทศแล้ว แจ้งที่พัก ตม.30 และรายงานตัว 90 วันตามกำหนด",
    ],
    processEn: [
      "Obtain a police clearance certificate and medical certificate in your country of residence",
      "Purchase compliant, verifiable health insurance",
      "File through the e-Visa system at the Thai mission covering your residence",
      "After arrival, ensure TM30 is filed and keep up 90-day reporting",
    ],
    extensionTh: "เมื่อครบกำหนด สามารถยื่น ตม.7 ขออยู่ต่อในไทยตามหลักเกณฑ์เกษียณอายุ ซึ่งใช้เงื่อนไขเงินฝาก 800,000 บาทหรือรายได้ 65,000 บาทต่อเดือน",
    extensionEn: "At the end of the period you can file TM.7 in Thailand under the retirement criteria — THB 800,000 on deposit or THB 65,000 monthly income.",
    pitfallsTh: [
      "ประกันสุขภาพไม่ตรงเงื่อนไขหรือไม่ครอบคลุมทั้งช่วงเวลา ทำให้ถูกปฏิเสธที่ด่านหรือตอนต่ออายุ",
      "เข้าใจผิดว่าวีซ่า O-A ให้พำนัก 2 ปีอัตโนมัติ — การเข้าใหม่ก่อนวีซ่าหมดอายุจึงจะได้รอบใหม่",
      "เอกสารรับรองความประพฤติหมดอายุก่อนวันยื่น",
    ],
    pitfallsEn: [
      "Insurance that does not meet the specification or lapses mid-period, causing refusal at entry or renewal",
      "Assuming O-A automatically gives two years — the second year comes from re-entering before the visa expires",
      "Police clearance certificate expiring before the filing date",
    ],
    authorityTh: "สถานเอกอัครราชทูต/สถานกงสุลไทย และสำนักงานตรวจคนเข้าเมือง",
    authorityEn: "Thai missions abroad and the Immigration Bureau.",
    sources: [
      { label: "Thai e-Visa (MFA)", url: "https://www.thaievisa.go.th" },
      { label: "Immigration Bureau", url: "https://www.immigration.go.th" },
    ],
  }),
  V({
    slug: "o-x-retirement",
    titleTh: "วีซ่าพำนักระยะยาว O-X (10 ปี)",
    titleEn: "Non-Immigrant O-X Long Stay (10 Years)",
    codeTh: "Non-O-X",
    codeEn: "Non-O-X",
    whoTh: "ผู้มีอายุ 50 ปีขึ้นไปที่ถือสัญชาติของประเทศในกลุ่มที่ทางการไทยกำหนดสำหรับ O-X และมีฐานะการเงินตามเกณฑ์สูงกว่ากลุ่ม O-A",
    whoEn: "Applicants aged 50+ holding a nationality on the designated O-X list, meeting higher financial thresholds than O-A.",
    validityTh: "อนุญาตพำนักรวมได้สูงสุด 10 ปี แบ่งเป็นสองช่วง ช่วงละ 5 ปี",
    validityEn: "Up to 10 years in total, granted as two consecutive 5-year periods.",
    stayTh: "เข้าออกได้หลายครั้งตลอดอายุการอนุญาต โดยยังต้องรายงานตัวทุก 90 วัน",
    stayEn: "Multiple entries throughout the permitted period; 90-day reporting still applies.",
    requirementsTh: [
      "อายุ 50 ปีขึ้นไป และถือสัญชาติในกลุ่มประเทศที่กำหนด",
      "เงินฝากในธนาคารไทย 3,000,000 บาท หรือเงินฝาก 1,800,000 บาทรวมกับรายได้ต่อปีไม่น้อยกว่า 1,200,000 บาท",
      "ประกันสุขภาพที่มีผลบังคับตลอดเวลาที่พำนักในไทย",
      "หนังสือรับรองความประพฤติและใบรับรองแพทย์",
      "ไม่มีสิทธิทำงานในไทยระหว่างพำนักด้วยวีซ่าประเภทนี้",
    ],
    requirementsEn: [
      "Aged 50+ and holding a nationality on the designated list",
      "THB 3,000,000 on deposit in Thailand, or THB 1,800,000 on deposit plus annual income of at least THB 1,200,000",
      "Health insurance valid throughout the stay",
      "Police clearance certificate and medical certificate",
      "No right to work in Thailand under this category",
    ],
    processTh: [
      "ตรวจว่าสัญชาติของท่านอยู่ในกลุ่มที่ยื่น O-X ได้",
      "โอนเงินเข้าบัญชีธนาคารไทยและรักษายอดตามเงื่อนไข",
      "ยื่นคำร้องจากต่างประเทศผ่านระบบ e-Visa",
      "เมื่อครบ 5 ปีแรก ยื่นขอต่ออีก 5 ปีที่สำนักงานตรวจคนเข้าเมือง",
    ],
    processEn: [
      "Confirm your nationality is eligible for O-X",
      "Transfer and maintain the required funds in a Thai bank account",
      "File from abroad through the e-Visa system",
      "After the first 5 years, apply at the Immigration Bureau for the second 5-year period",
    ],
    extensionTh: "ช่วงที่สองอีก 5 ปียื่นในไทย โดยต้องคงเงื่อนไขเงินฝากและประกันสุขภาพต่อเนื่อง",
    extensionEn: "The second five-year period is granted in Thailand, provided the deposit and insurance conditions are maintained.",
    pitfallsTh: [
      "ถอนเงินก้อนออกก่อนครบเงื่อนไข ทำให้ถูกเพิกถอนสิทธิ",
      "ประกันสุขภาพขาดช่วง",
      "ทำงานหรือรับค่าตอบแทนในไทย ซึ่งไม่อนุญาตในวีซ่าประเภทนี้",
    ],
    pitfallsEn: [
      "Withdrawing the deposit before the conditions are met, risking revocation",
      "Allowing health insurance to lapse",
      "Working or earning in Thailand, which this category does not allow",
    ],
    authorityTh: "สถานเอกอัครราชทูต/สถานกงสุลไทย และสำนักงานตรวจคนเข้าเมือง",
    authorityEn: "Thai missions abroad and the Immigration Bureau.",
    sources: [
      { label: "Thai e-Visa (MFA)", url: "https://www.thaievisa.go.th" },
      { label: "Immigration Bureau", url: "https://www.immigration.go.th" },
    ],
  }),
  V({
    slug: "dtv",
    titleTh: "วีซ่า DTV (Destination Thailand Visa)",
    titleEn: "Destination Thailand Visa (DTV)",
    codeTh: "DTV",
    codeEn: "DTV",
    whoTh: "ผู้ทำงานทางไกลให้นายจ้างหรือลูกค้าในต่างประเทศ (remote worker/ดิจิทัลโนแมด) ผู้เข้าร่วมกิจกรรม Soft Power เช่น เรียนมวยไทย ทำอาหารไทย รักษาพยาบาล และผู้ติดตามในครอบครัว",
    whoEn: "Remote workers employed by or serving clients outside Thailand, participants in soft-power activities such as Muay Thai, Thai cooking and medical treatment, plus accompanying dependants.",
    validityTh: "อายุวีซ่า 5 ปี เข้าออกได้หลายครั้ง",
    validityEn: "Five years of validity with multiple entries.",
    stayTh: "แต่ละครั้งที่เข้าประเทศอยู่ได้ไม่เกิน 180 วัน และขอขยายได้อีกไม่เกิน 180 วันหนึ่งครั้งต่อการเข้าหนึ่งครั้ง",
    stayEn: "Each entry allows up to 180 days, extendable once by up to a further 180 days per entry.",
    requirementsTh: [
      "หลักฐานทางการเงินไม่น้อยกว่า 500,000 บาท",
      "หลักฐานคุณสมบัติตามกลุ่มที่ยื่น เช่น สัญญาจ้างหรือหนังสือรับรองการทำงานทางไกล ใบลงทะเบียนหลักสูตร หรือเอกสารนัดหมายทางการแพทย์",
      "หลักฐานความเกี่ยวข้องสำหรับผู้ติดตาม เช่น ทะเบียนสมรสหรือสูติบัตร ซึ่งมักต้องแปลและรับรอง",
      "หนังสือเดินทางอายุคงเหลือไม่น้อยกว่า 6 เดือน",
    ],
    requirementsEn: [
      "Financial evidence of at least THB 500,000",
      "Category evidence: remote employment contract or client engagement letter, course enrolment, or medical appointment documents",
      "Relationship documents for dependants such as marriage or birth certificates, usually translated and certified",
      "Passport valid at least 6 months",
    ],
    processTh: [
      "เลือกกลุ่มคุณสมบัติ (Workcation หรือ Thai Soft Power) ให้ตรงกับหลักฐานที่มี",
      "ยื่นผ่านระบบ e-Visa จากประเทศที่พำนักหรือประเทศที่มีถิ่นที่อยู่",
      "เมื่อเข้าประเทศ ตรวจตราประทับให้แน่ใจว่าได้รับ 180 วัน",
      "หากต้องการขยาย ยื่น ตม.7 ที่สำนักงานตรวจคนเข้าเมืองก่อนหมดอายุ",
    ],
    processEn: [
      "Pick the qualifying track (workcation or Thai soft power) matching your evidence",
      "File through the e-Visa system from your country of residence",
      "On arrival, check the stamp shows the expected 180 days",
      "To extend, file TM.7 at immigration before the stamp expires",
    ],
    extensionTh: "ขยายได้ครั้งละไม่เกิน 180 วันต่อการเข้าหนึ่งครั้ง โดยยื่น ตม.7 การอนุมัติเป็นดุลพินิจของพนักงานเจ้าหน้าที่",
    extensionEn: "Up to 180 additional days per entry via TM.7, subject to officer discretion.",
    pitfallsTh: [
      "เข้าใจว่า DTV ทำงานให้บริษัทไทยได้ — ไม่ได้ การรับงานในไทยต้องมีใบอนุญาตทำงาน",
      "หลักฐานการเงินไม่ได้อยู่ในชื่อผู้ยื่นหรือไม่ครบระยะเวลา",
      "อยู่ในไทยยาวต่อเนื่องโดยไม่ตรวจภาระภาษีตามหลักการพำนัก 180 วันในปีภาษี",
      "เอกสารผู้ติดตามไม่ได้ผ่านนิติกรณ์และแปลตามที่กำหนด",
    ],
    pitfallsEn: [
      "Believing DTV permits work for Thai companies — it does not; local work needs a work permit",
      "Financial evidence not in the applicant's name or not held long enough",
      "Staying long-term without checking Thai tax residency at 180 days in a tax year",
      "Dependant documents not legalised and translated as required",
    ],
    authorityTh: "กระทรวงการต่างประเทศ (ออกวีซ่า) และสำนักงานตรวจคนเข้าเมือง (การอยู่ต่อ)",
    authorityEn: "Ministry of Foreign Affairs issues the visa; the Immigration Bureau handles extensions.",
    sources: [
      { label: "Thai e-Visa (MFA)", url: "https://www.thaievisa.go.th" },
      { label: "Immigration Bureau", url: "https://www.immigration.go.th" },
    ],
  }),
  V({
    slug: "ltr",
    titleTh: "วีซ่าผู้พำนักระยะยาว LTR (10 ปี)",
    titleEn: "Long-Term Resident Visa (LTR, 10 Years)",
    codeTh: "LTR",
    codeEn: "LTR",
    whoTh: "ผู้มีคุณสมบัติ 4 กลุ่มตามที่สำนักงานคณะกรรมการส่งเสริมการลงทุน (BOI) กำหนด ได้แก่ ผู้มีความมั่งคั่งสูง ผู้เกษียณอายุที่มีฐานะมั่นคง ผู้ทำงานทางไกลให้บริษัทต่างประเทศ และผู้เชี่ยวชาญทักษะสูง",
    whoEn: "Four BOI-defined groups: wealthy global citizens, wealthy pensioners, work-from-Thailand professionals, and highly skilled professionals.",
    validityTh: "สูงสุด 10 ปี (อนุมัติ 5 ปี และต่ออายุอีก 5 ปีเมื่อยังคงคุณสมบัติ)",
    validityEn: "Up to 10 years, issued as 5 years plus a 5-year renewal while eligibility continues.",
    stayTh: "เข้าออกได้หลายครั้ง และรายงานตัวปีละครั้งแทนการรายงานตัวทุก 90 วัน",
    stayEn: "Multiple entries, with annual reporting instead of 90-day reporting.",
    requirementsTh: [
      "คุณสมบัติทางการเงินและรายได้ตามกลุ่มที่ยื่น ซึ่ง BOI ประกาศไว้แตกต่างกันในแต่ละกลุ่ม",
      "ประกันสุขภาพหรือหลักฐานเงินฝากตามเงื่อนไขที่กำหนด",
      "หลักฐานการจ้างงาน ผลประกอบการของนายจ้างต่างประเทศ หรือหลักฐานการลงทุน แล้วแต่กลุ่ม",
      "เอกสารการศึกษาและประสบการณ์สำหรับกลุ่มผู้เชี่ยวชาญทักษะสูง ซึ่งมักต้องรับรองและแปล",
    ],
    requirementsEn: [
      "Financial and income criteria specific to the category, as published by BOI",
      "Health insurance or deposit evidence per the applicable condition",
      "Employment, overseas employer performance or investment evidence, depending on the category",
      "Education and experience documents for highly skilled professionals, usually certified and translated",
    ],
    processTh: [
      "ยื่นคำขอรับรองคุณสมบัติผ่านระบบออนไลน์ของ BOI",
      "BOI พิจารณาและออกหนังสือรับรองคุณสมบัติ (Qualification Endorsement)",
      "รับการตรวจลงตรา LTR ที่สถานเอกอัครราชทูต/สถานกงสุล หรือที่ศูนย์บริการวีซ่าและใบอนุญาตทำงานในไทย",
      "ผู้เข้าเงื่อนไขสามารถขอใบอนุญาตทำงานแบบดิจิทัลควบคู่กับ LTR ได้",
    ],
    processEn: [
      "File the qualification application on the BOI online system",
      "BOI reviews and issues the qualification endorsement",
      "Collect the LTR visa at a Thai mission or at the One Stop Service Center in Thailand",
      "Eligible holders can obtain the accompanying digital work permit",
    ],
    extensionTh: "ต่ออายุอีก 5 ปีเมื่อครบกำหนด โดยต้องยังคงคุณสมบัติของกลุ่มที่ได้รับอนุมัติ และรายงานตัวประจำปีตามกำหนด",
    extensionEn: "Renewed for a further 5 years while the category criteria continue to be met, with annual reporting.",
    pitfallsTh: [
      "ยื่นผิดกลุ่มคุณสมบัติ ทำให้เอกสารทางการเงินไม่ตรงเกณฑ์",
      "หลักฐานรายได้ย้อนหลังไม่ครบปีตามที่ BOI กำหนด",
      "เข้าใจว่า LTR ให้สิทธิทำงานอัตโนมัติ — ใบอนุญาตทำงานเป็นขั้นตอนแยก",
    ],
    pitfallsEn: [
      "Applying under the wrong category so the financial evidence does not match",
      "Income history short of the number of years BOI requires",
      "Assuming LTR includes work rights automatically — the digital work permit is a separate step",
    ],
    authorityTh: "สำนักงานคณะกรรมการส่งเสริมการลงทุน (BOI) ร่วมกับสำนักงานตรวจคนเข้าเมือง",
    authorityEn: "Thailand Board of Investment with the Immigration Bureau.",
    sources: [
      { label: "BOI LTR Visa", url: "https://ltr.boi.go.th" },
      { label: "Thailand BOI", url: "https://www.boi.go.th" },
    ],
  }),
  V({
    slug: "smart-visa",
    titleTh: "SMART Visa (ผู้เชี่ยวชาญ นักลงทุน ผู้บริหาร สตาร์ทอัพ)",
    titleEn: "SMART Visa (Talent, Investor, Executive, Startup)",
    codeTh: "SMART T / I / E / S / O",
    codeEn: "SMART T / I / E / S / O",
    whoTh: "ผู้เชี่ยวชาญทักษะสูง (T) นักลงทุน (I) ผู้บริหารระดับสูง (E) ผู้ประกอบการสตาร์ทอัพ (S) และคู่สมรส/บุตร (O) ในอุตสาหกรรมเป้าหมายที่ทางการกำหนด",
    whoEn: "Highly skilled talent (T), investors (I), executives (E), startup entrepreneurs (S) and their spouse/children (O) in the targeted industries.",
    validityTh: "อนุญาตพำนักได้สูงสุด 4 ปี ขึ้นกับประเภทและสัญญาจ้าง",
    validityEn: "Up to 4 years depending on the sub-category and contract length.",
    stayTh: "ผู้ถือ SMART Visa ในกลุ่มที่กำหนดไม่ต้องขอใบอนุญาตทำงานแยก และรายงานตัวปีละครั้ง",
    stayEn: "Qualifying SMART holders do not need a separate work permit and report annually instead of every 90 days.",
    requirementsTh: [
      "หนังสือรับรองคุณสมบัติจากหน่วยงานผู้รับรองในอุตสาหกรรมเป้าหมาย",
      "สัญญาจ้างหรือหลักฐานการลงทุน/การถือหุ้นตามเกณฑ์ของแต่ละประเภท",
      "หลักฐานรายได้ขั้นต่ำตามที่ประกาศกำหนดสำหรับกลุ่ม T และ E",
      "ประกันสุขภาพครอบคลุมตลอดระยะเวลาพำนัก",
    ],
    requirementsEn: [
      "Qualification endorsement from the relevant agency in the targeted industry",
      "Employment contract or investment/shareholding evidence per sub-category",
      "Minimum income evidence as announced for the T and E categories",
      "Health insurance covering the full stay",
    ],
    processTh: [
      "ยื่นคำขอรับรองคุณสมบัติผ่านศูนย์บริการ SMART Visa ของ BOI",
      "หน่วยงานผู้รับรองพิจารณาคุณสมบัติทางเทคนิคและอุตสาหกรรมเป้าหมาย",
      "รับการตรวจลงตราที่สถานเอกอัครราชทูต/สถานกงสุล หรือศูนย์บริการวีซ่าและใบอนุญาตทำงาน",
      "รายงานตัวประจำปีและแจ้งเปลี่ยนแปลงนายจ้างตามเงื่อนไข",
    ],
    processEn: [
      "File for qualification endorsement at the BOI SMART Visa unit",
      "The endorsing agency assesses technical and industry criteria",
      "Collect the visa at a Thai mission or the One Stop Service Center",
      "Report annually and notify any change of employer as required",
    ],
    extensionTh: "ต่ออายุตามระยะเวลาสัญญาจ้างหรือแผนธุรกิจที่ได้รับการรับรอง โดยต้องคงคุณสมบัติของประเภทที่ได้รับอนุมัติ",
    extensionEn: "Renewed in line with the endorsed contract or business plan while the category criteria are maintained.",
    pitfallsTh: [
      "อุตสาหกรรมของนายจ้างไม่อยู่ในกลุ่มเป้าหมายที่กำหนด",
      "เปลี่ยนนายจ้างโดยไม่ยื่นรับรองคุณสมบัติใหม่",
      "เอกสารวุฒิและประสบการณ์ไม่ได้รับรองตามที่หน่วยงานผู้รับรองต้องการ",
    ],
    pitfallsEn: [
      "The employer's industry falling outside the targeted list",
      "Changing employer without a new endorsement",
      "Degree and experience documents not certified as the endorsing agency requires",
    ],
    authorityTh: "ศูนย์บริการ SMART Visa สำนักงานคณะกรรมการส่งเสริมการลงทุน (BOI) และหน่วยงานผู้รับรอง",
    authorityEn: "BOI SMART Visa Unit and the endorsing agencies.",
    sources: [
      { label: "SMART Visa (BOI)", url: "https://smart-visa.boi.go.th" },
      { label: "Thailand BOI", url: "https://www.boi.go.th" },
    ],
  }),
];

export function getThaiVisaType(slug: string): ThaiVisaType | undefined {
  return THAI_VISA_TYPES.find((v) => v.slug === slug);
}